import { Text } from '@dixa/lib-shared-habla';
import type { SearchFilterCondition } from 'lib-shared-types-api';
import { FunctionComponent, useMemo } from 'react';

import { ConditionFieldCustomAttribute } from './components/customAttribute/conditionFieldCustomAttribute';
import { ConditionFieldDate } from './components/date/conditionFieldDate';
import { ConditionFieldDuration } from './components/duration/conditionFieldDuration';
import {
  ConditionFieldAgents,
  ConditionFieldChannel,
  ConditionFieldCompany,
  ConditionFieldContacts,
  ConditionFieldDirection,
  ConditionFieldId,
  ConditionFieldQueue,
  ConditionFieldStatus,
  ConditionFieldTags,
  ConditionFieldUserType,
} from './components/select/conditionFieldSelect';

/** Represents the props passed to a filter condition field. */
export type ConditionFieldProps<TCondition extends SearchFilterCondition> = {
  /** The current filter condition. */
  condition: TCondition;
  /** The placeholder to show in the filter component input. */
  placeholder?: string;
  /** Whether the filter component input is disabled. */
  disabled?: boolean;
  /** The callback called when the value of the filter component input changes. */
  onChange: (values: TCondition['values']) => void;
  /** The callback called when an error occurs in a filter component. */
  onError?: (error: string) => void;
};

export const ConditionField = <TCondition extends SearchFilterCondition>(
  props: ConditionFieldProps<TCondition>,
): JSX.Element => {
  const Component = useMemo(() => {
    switch (props.condition.field) {
      case 'numeric_id':
        return ConditionFieldId;
      case 'status':
        return ConditionFieldStatus;
      case 'initial_direction':
        return ConditionFieldDirection;
      case 'initial_channel':
        return ConditionFieldChannel;
      case 'queue_id':
        return ConditionFieldQueue;
      case 'agent_id':
        return ConditionFieldAgents;
      case 'requested_by':
        return ConditionFieldContacts;
      case 'tags':
        return ConditionFieldTags;
      case 'company_id':
        return ConditionFieldCompany;
      case 'user_type':
        return ConditionFieldUserType;
      case 'duration':
        return ConditionFieldDuration;

      case 'assigned_at':
      case 'closed_at':
      case 'created_at':
      case 'updated_at':
      case 'last_message_created_at':
        return ConditionFieldDate;

      case 'conversation_custom_attribute':
      case 'contact_custom_attribute':
        return ConditionFieldCustomAttribute;

      default:
        return Fallback;
    }
  }, [props.condition.field]) as FunctionComponent<
    ConditionFieldProps<TCondition>
  >;

  return <Component {...props} />;
};

const Fallback: FunctionComponent = () => (
  <Text.Paragraph variant="4" subdued>
    Not implemented
  </Text.Paragraph>
);
